import { DeleteOutlined } from "@ant-design/icons";
import { Tag as TagDTO } from '@dto/tag.dto';
import { Button } from "antd";
import { FC, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import type { FilterTag, SubsystemClassificationItem } from '../search.types';
import { FilterBadge } from './FilterBadge';
import '../styles/FilterBadgeList.less';

interface FilterBadgeListProps {
  filters: FilterTag[];
  onRemove: (index: number) => void;
  onSave: (index: number, updatedFilter: FilterTag) => void;
  onSubmit?: () => void;
  onClearAll: () => void;
  focusedBadgeIndex: number | null;
  focusTarget?: 'editor' | 'remove';
  onBadgeKeyDown: (e: KeyboardEvent<Element>, index: number) => void;
  onClearAllKeyDown: (e: KeyboardEvent<Element>) => void;
  availableTags: TagDTO[];
  availableTagsCategories: TagDTO[];
  availableArchitecture: SubsystemClassificationItem[];
}

export const FilterBadgeList: FC<FilterBadgeListProps> = ({
  filters,
  onRemove,
  onSave,
  onSubmit,
  onClearAll,
  focusedBadgeIndex,
  focusTarget,
  onBadgeKeyDown,
  onClearAllKeyDown,
  availableTags,
  availableTagsCategories,
  availableArchitecture,
}) => {
  const [removingIndex, setRemovingIndex] = useState<number | null>(null);
  const [isClearingAll, setIsClearingAll] = useState(false);
  const removingTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const clearAllTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const clearAllRef = useRef<HTMLButtonElement>(null);
  const isClearAllFocused = focusedBadgeIndex === filters.length;

  useEffect(() => {
    if (isClearAllFocused) {
      clearAllRef.current?.focus();
    }
  }, [isClearAllFocused]);

  useEffect(() => {
    return () => {
      clearTimeout(removingTimerRef.current);
      clearTimeout(clearAllTimerRef.current);
    };
  }, []);

  const handleAnimatedRemove = useCallback(
    (index: number) => {
      clearTimeout(removingTimerRef.current);
      setRemovingIndex(index);
      removingTimerRef.current = setTimeout(() => {
        setRemovingIndex(null);
        onRemove(index);
      }, 150);
    },
    [onRemove]
  );

  const handleClearAllAnimated = useCallback(() => {
    clearTimeout(clearAllTimerRef.current);
    setIsClearingAll(true);
    clearAllTimerRef.current = setTimeout(() => {
      setIsClearingAll(false);
      onClearAll();
    }, 150);
  }, [onClearAll]);

  const handleClearAllKeyDownWrapped = useCallback((e: KeyboardEvent<Element>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClearAllAnimated();
      return;
    }
    onClearAllKeyDown(e);
  }, [handleClearAllAnimated, onClearAllKeyDown]);

  const handleKeyDown = useCallback(
    (index: number) => (e: KeyboardEvent<Element>) => {
      onBadgeKeyDown(e, index);
    },
    [onBadgeKeyDown]
  );

  if (filters.length === 0) return null;

  return (
    <div className='xm-filter-badge-list'>
      <div className='xm-filter-badge-list__items'>
        {filters.map((filter, index) => (
          <FilterBadge
            key={filter.type}
            filter={filter}
            isFocused={focusedBadgeIndex === index}
            focusTarget={focusedBadgeIndex === index ? focusTarget : undefined}
            isRemoving={isClearingAll || removingIndex === index}
            onRemove={() => handleAnimatedRemove(index)}
            onSave={(updatedFilter) => onSave(index, updatedFilter)}
            onSubmit={onSubmit}
            onKeyDown={handleKeyDown(index)}
            availableTags={availableTags}
            availableTagsCategories={availableTagsCategories}
            availableArchitecture={availableArchitecture}
          />
        ))}
      </div>
      <div className='xm-filter-badge-list__footer'>
        <Button
          ref={clearAllRef}
          className={`xm-filter-badge-list__clear-btn hover-danger${isClearAllFocused ? ' xm-filter-badge-list__clear-btn--focused' : ''}`}
          onClick={handleClearAllAnimated}
          onKeyDown={handleClearAllKeyDownWrapped}
          type='link'
        >
          <DeleteOutlined />
          Clear all filters
        </Button>
      </div>
    </div>
  );
};
